/**
 * Hook to generate a modal queue, with a specified priority.
 *
 * The input of the hook is an object with Modal IDs as the keys and a priority
 * number as the value. The priority will affect the order that modals display
 * if more than one is queued to display at a time. Modal IDs that are not
 * present will have a priority level of zero, which means they'll display in
 * the order that they were queued.
 *
 * The output of the hook is similar in structure to React's `useState`. The
 * first element in the array is the current modal's ID, or -1 if no modal is
 * open. The second element is a function that generates an opener.
 *
 * ```
 * enum ModalId {
 *   Geolocation,
 *   Petition
 * }
 *
 * const priorities = {
 *   [ModalId.Geolocation]: 1
 * };
 *
 * function Modals() {
 *   const [currentModalId, generateOpener] = useModalQueue(priorities);
 *
 *   const {
 *     setGeolocationModalOpen
 *   } = useMemo(() => ({
 *     setGeolocationModalOpen: generateOpener(ModalId.Geolocation)
 *   }, [generateOpener])
 *
 *   const isGeolocationModalOpen = currentModalId === ModalId.Geolocation;
 *
 *   const onClick = () => setGeolocationModalOpen(true);
 *
 *   return (
 *     <>
 *       <button type="button" onClick={() => setGeolocationModalOpen(true)}>
 *         Open Geolocation
 *       </button>
 *       {isGeolocationModalOpen && (
 *         <div>Geolocation Modal</div>
 *       )}
 *     </>
 *   )
 * ```
 */

import { useMemo, useState } from 'react';
import ModalQueue from './ModalQueue';

export type GeneratedOpener = (_: boolean) => void;

const useModalQueue = (
  priorities: Record<number, number> = {}
): [number, (id: number) => GeneratedOpener, ModalQueue] => {
  const [currentModalId, setCurrentModalId] = useState(-1);

  const queue = useMemo(() => new ModalQueue(setCurrentModalId), []);

  const generateOpener = useMemo(
    () => (id: number) => (isOpen: boolean) => {
      if (isOpen) {
        queue.enqueue(id, priorities[id] ?? 0);
      } else {
        queue.dequeue(id);
      }
    },
    [priorities, queue]
  );

  return [currentModalId, generateOpener, queue];
};

export default useModalQueue;
